import { ExtendedSecondaryStatusRoboticMowerEnum } from '@uptime-bff/api-contract';
import { breakpoints, darkTheme } from 'hfs-common-ui';
import { DefaultTheme } from 'styled-components';
import { ExtendedSecondaryStatusRoboticMowerEnum as SecondaryStatusRoboticMowerEnum } from 'core/models/backend-translations-types';

export const colors = {
  husqvarnaBlue: '#273A60',
};

export const hudskitColors = {
  grey400: 'grey400',
  grey800: 'grey800',
};

/**
 * Get css color of the combination of two stacked colors
 * @param colorFront Top color, normally used for success, error, caution, progress, etc.
 * @param colorBack Back color, normally used for background or surface color
 * @param opacity Opacity Level by default 50%
 * @returns CSS for the color mix
 */
export const colorMix = (colorFront: string, colorBack: string, opacity = 50) =>
  `color-mix(in srgb, ${colorFront} ${opacity}%,  ${colorBack})`;

export const paddings = {
  hugePadding: '32px',
  largePadding: '20px',
  defaultPadding: '16px',
  mediumPadding: '12px',
  mediumSmallPadding: '8px',
  smallPadding: '6px',
  tinyPadding: '4px',
};

export const borderRadius = {
  default: '16px',
  round: '50%',
};

export const device = {
  xs: `(max-width: ${breakpoints.s}px)`,
  sm: `(min-width: ${breakpoints.s}px)`,
  md: `(min-width: ${breakpoints.m}px)`,
  lg: `(min-width: ${breakpoints.l}px)`,
  xl: `(min-width: ${breakpoints.xl}px)`,
  xxl: `(min-width: ${breakpoints.xxl}px)`,
};

const typography = {
  type: {
    primary: '"Husqvarna Gothic", "Helvetica Neue", Helvetica, Arial, sans-serif',
  },
  weight: {
    light: '300',
    regular: '400',
    bold: '700',
  },
};

export const typographyMobile = {
  ...typography,
  size: {
    large: '1.25rem',
    medium: '1.125rem',
    default: '1rem',
    small: '0.875rem',
    tiny: '0.75rem',
    h1: '2.5rem',
    h2: '1.75rem',
    h3: '1.5rem',
    h4: '1.25rem',
    h5: '1.125rem',
    h6: '1rem',
  },
};

export const typographyDesktop = {
  ...typography,
  size: {
    large: '1.375rem',
    medium: '1.25rem',
    default: '1.125rem',
    small: '1rem',
    tiny: '0.875rem',
    h1: '2.875rem',
    h2: '2.125rem',
    h3: '1.875rem',
    h4: '1.625rem',
    h5: '1.5rem',
    h6: '1.375rem',
  },
};

export const headerHeight = '77px';

export type FunctionalColor = 'error' | 'caution' | 'success' | 'progress' | 'connectivity' | 'other' | 'none';
/**
 * Gets the RGB color for the given functional color
 * @param theme Current active theme
 * @param color Functional Color to render
 * @returns RGB Color from theme from the specified Functional Color
 */
export const getFunctionalColor = (theme: DefaultTheme, color?: FunctionalColor) => {
  switch (color) {
    case 'error':
      return theme.colors.red500;
    case 'caution':
      return theme.colors.yellow500;
    case 'success':
      return theme.colors.green500;
    case 'progress':
      return theme.colors.petroleum500;
    case 'connectivity':
      return theme.colors.blue500;
    case 'other':
      return theme === darkTheme ? theme.colors.grey200 : theme.colors.grey400;
    default:
      return 'inherit';
  }
};

/**
 * Gets the appropriate background color for the given combination of functional color and surface/background color
 * @param theme Current active theme
 * @param color Functional color to render
 * @param surface Render on a surface or on background
 * @param opacity Opacity percentage
 * @returns CSS color Mix with the provided functional color and the background
 */
export const getFunctionalLightColor = (theme: DefaultTheme, color?: FunctionalColor, surface = true, opacity = 50) => {
  const functionalColor = getFunctionalColor(theme, color);
  return colorMix(functionalColor, surface ? theme.colors.surface : theme.colors.background, opacity);
};

export const getForegroundFunctionalColor = (theme: DefaultTheme, color?: FunctionalColor) => {
  switch (color) {
    case 'error':
    case 'success':
    case 'progress':
    case 'connectivity':
      return theme === darkTheme ? theme.colors.grey300 : theme.colors.grey200;
    case 'caution':
      return theme === darkTheme ? theme.colors.grey600 : theme.colors.grey700;
    default:
      return 'inherit';
  }
};

/**
 * Gets the RGB color for the given Status color
 * @param theme Current active theme
 * @param color Functional Color base on Status to render
 * @returns RGB Color from theme from the specified Functional Color
 */
export const getColorBasedOnStatus = (theme: DefaultTheme, status: ExtendedSecondaryStatusRoboticMowerEnum) => {
  switch (status) {
    case SecondaryStatusRoboticMowerEnum.ERROR:
    case SecondaryStatusRoboticMowerEnum.THEFT_ALARM:
      return theme.colors.red500;
    case SecondaryStatusRoboticMowerEnum.STOPPED:
    case SecondaryStatusRoboticMowerEnum.PAUSED:
      return theme.colors.yellow500;
    case SecondaryStatusRoboticMowerEnum.MOWING:
    case SecondaryStatusRoboticMowerEnum.TRANSPORTING:
      return theme.colors.green500;
    case SecondaryStatusRoboticMowerEnum.PARKED_TEMPORARILY:
    case SecondaryStatusRoboticMowerEnum.PARKED:
      return theme.colors.petroleum500;
    case SecondaryStatusRoboticMowerEnum.OPERATING:
      return theme.colors.green500;
    case SecondaryStatusRoboticMowerEnum.UNKNOWN:
    case SecondaryStatusRoboticMowerEnum.TURNED_OFF:
    case SecondaryStatusRoboticMowerEnum.PREPARING:
      return theme === darkTheme ? theme.colors.grey600 : theme.colors.grey200;
    default:
      return 'inherit';
  }
};

export const displayDateTimeFormat = 'yyyy-MM-dd HH:mm';
export const displayDateFormat = 'yyyy-MM-dd';
